/**
 * State for one framed gateway page (#939, Feature H): the frame's address, what it has told
 * the Marketplace so far, its height, and a way to load it again.
 *
 * A frame is "loading" until its page says `ready`. A signed-out user is sent to a sign-in
 * page that refuses framing, so for them `ready` never comes: after FRAME_TIMEOUT_MS the
 * state becomes "no-response", and each caller decides what that means (My Connections offers
 * sign-in; the recent-use box simply stays hidden).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { manageFrameUrl, parseGatewayMessage, type ManagePage } from "./mcp-gateway";
import { reportEvent } from "./newrelic";
import { useResolvedTheme } from "./theme-context";

/** How long to wait for `ready` before treating the page as not answering. */
export const FRAME_TIMEOUT_MS = 15000;

export type GatewayFrameState =
  | { phase: "loading" }
  | { phase: "ready"; count: number; empty: boolean }
  | { phase: "error"; reason: string; title: string }
  | { phase: "no-response" };

export interface GatewayFrame {
  /** The frame's address, or null when no gateway is configured. */
  src: string | null;
  state: GatewayFrameState;
  /** The page's own height in CSS pixels, once it has reported one. */
  height: number | null;
  /** Attach to the iframe, so only messages from that window are read. */
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  /** Changes on every reload; use as the iframe's key so it loads afresh. */
  attempt: number;
  reload: () => void;
}

export function useGatewayFrame(page: ManagePage, serverUrl?: string): GatewayFrame {
  const theme = useResolvedTheme();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<GatewayFrameState>({ phase: "loading" });
  const [height, setHeight] = useState<number | null>(null);

  const src = useMemo(() => manageFrameUrl(page, { theme, serverUrl }), [page, theme, serverUrl]);

  useEffect(() => {
    setState({ phase: "loading" });
    setHeight(null);
    if (!src) return;

    // True once the page has answered (or the wait ran out), so the timer can't overwrite it.
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      timer = null;
      if (settled) return;
      settled = true;
      reportEvent("mcp-gateway-frame", { page, outcome: "no-response" });
      setState({ phase: "no-response" });
    }, FRAME_TIMEOUT_MS);
    const settle = () => {
      settled = true;
      if (timer) clearTimeout(timer);
      timer = null;
    };

    const onMessage = (event: MessageEvent) => {
      const parsed = parseGatewayMessage(event, iframeRef.current?.contentWindow);
      // The gateway's own error page reports itself as page "error"; anything else must be
      // the page this frame asked for.
      if (!parsed || (parsed.page !== page && parsed.page !== "error")) return;
      const { message } = parsed;
      switch (message.type) {
        case "ready":
          settle();
          setState({ phase: "ready", count: message.count, empty: message.count === 0 });
          break;
        case "empty":
          settle();
          setState((current) => ({ phase: "ready", count: current.phase === "ready" ? current.count : 0, empty: true }));
          break;
        case "height":
          setHeight(message.height);
          break;
        case "error":
          settle();
          reportEvent("mcp-gateway-frame", { page, outcome: "error", reason: message.reason });
          setState({ phase: "error", reason: message.reason, title: message.title });
          break;
        default:
          break;
      }
    };
    window.addEventListener("message", onMessage);
    return () => {
      settle();
      window.removeEventListener("message", onMessage);
    };
  }, [src, page, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { src, state, height, iframeRef, attempt, reload };
}
